import React, { ChangeEvent, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, SortingState, useReactTable } from '@tanstack/react-table';
import ViewSummary from "../components/Models/ViewSummary";
import InspectionModal from "../components/Models/InspectionModal";
import { inspectionService, InspectionHistory } from "../services/inspection.service";
import { format } from "date-fns";
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/reducers';
import { actions } from '../store/actions';
import { useInspectionPolling } from '../hooks/useInspectionPolling';

function InspectionPage(): React.ReactElement {
    const dispatch = useDispatch();
    const [viewSummary, setViewSummary] = useState(false);
    const [showInspectionModal, setShowInspectionModal] = useState(false);
    const [showSummary, setShowSummary] = useState(false);
    const [selectedInspection, setSelectedInspection] = useState<InspectionHistory | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [search, setSearch] = useState<string>('');

    // Get state from Redux
    const { inspectionHistory, currentInspection } = useSelector((state: RootState) => state.inspection);
    const columnHelper = createColumnHelper<InspectionHistory>();

    // Use the inspection polling hook to get real-time updates
    const { fetchInProgressInspection, checkInspectionImmediately } = useInspectionPolling();

    useEffect(() => {
        fetchInspectionHistory();
    }, [currentInspection]);

    const fetchInspectionHistory = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await inspectionService.getInspectionHistory();
            dispatch(actions.setInspectionHistory(data));
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to fetch inspection history');
            console.error('Failed to fetch inspection history:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleViewSummary = (inspection: InspectionHistory) => {
        setSelectedInspection(inspection);
        setViewSummary(true);
    };

    const handleStartInspection = () => {
        setShowInspectionModal(true);
    };

    const handleInspectionCreated = () => {
        // Refresh the inspection history and immediately check for current inspection
        fetchInspectionHistory();
        checkInspectionImmediately();
    };



    // Column definitions
    const columns = [
        columnHelper.accessor('id', {
            header: 'Inspection ID',
            cell: (info) => (
                <span className="font-[Geist-Medium] text-black-1">
                    {info.getValue()}
                </span>
            )
        }),
        columnHelper.accessor('createdAt', {
            header: 'Date & Time',
            cell: (info) => (
                <span className="font-[Geist-Medium] text-black-1 whitespace-nowrap">
                    {format(new Date(info.getValue()), "dd MMM yyyy, hh:mm a")}
                </span>
            )
        }),
        columnHelper.accessor('trailerId', {
            header: 'Trailer ID',
            cell: (info) => (
                <span className="font-[Geist-Medium] text-black-1">
                    {info.getValue()}
                </span>
            )
        }),
        columnHelper.accessor('status', {
            header: 'Status',
            cell: (info) => getStatusBadge(info.getValue())
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => (
                <button
                    onClick={() => handleViewSummary(row.original)}
                    className="text-green hover:underline text-sm font-medium font-[Geist-Medium]"
                >
                    View Summary
                </button>
            )
        })
    ];

    const table = useReactTable({
        data: inspectionHistory,
        columns: columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: setSorting,
        getFilteredRowModel: getFilteredRowModel(),
        onGlobalFilterChange: setSearch,
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 6,
            }
        },
        state: {
            sorting: sorting,
            globalFilter: search
        }
    });

    if (loading) {
        return (
            <div className="w-full h-full bg-white rounded-xl border border-gray-3 flex items-center justify-center">
                <div className="text-gray-2">Loading inspection history...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full h-full bg-white rounded-xl border border-gray-3 flex items-center justify-center">
                <div className="text-red-500">
                    {error}
                    <button onClick={fetchInspectionHistory} className="ml-2 underline">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full bg-white rounded-xl border border-gray-3 flex flex-col gap-6">
            {/* Header Section */}
            <div className="w-full flex flex-col gap-3 p-4 pl-8">
                <div className="w-full flex justify-between items-center">
                    <h2 className="font-[Geist-Bold] text-lg md:text-xl text-green">
                        Previous Inspection
                    </h2>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleStartInspection}
                            className="flex items-center gap-2 bg-green hover:bg-green-600 text-white px-4 py-2 rounded transition-colors"
                        >
                            <Icon icon="material-symbols:add" className="text-lg" />
                            <span className="font-[Geist-Medium] text-sm">
                                Start Inspection
                            </span>
                        </button>
                    </div>
                </div>

                {/* Search Input */}
                <div className="relative">
                    <input
                        onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
                        value={search}
                        type="text"
                        className="w-full max-w-sm border border-gray-3 rounded px-4 py-2 pl-10 focus:outline-none focus:border-green font-[Geist-Medium]"
                        placeholder="Search inspection history..."
                    />
                    <Icon
                        icon="ri:search-line"
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-2 text-xl"
                    />
                </div>
            </div>

            {/* Table Section */}
            <div className="w-full px-3 flex-1 overflow-hidden flex flex-col">
                {inspectionHistory.length > 0 ? (
                    <div className="w-full border-t border-gray-3 h-full flex flex-col">
                        <div className="flex-1 overflow-y-auto" style={{
                            maxHeight: 'calc(100vh - 400px)',
                            minHeight: '300px'
                        }}>
                            <table className="w-full">
                                <thead className="sticky top-0 bg-white z-10">
                                    {table.getHeaderGroups().map(headerGroup => (
                                        <tr key={headerGroup.id} className="h-16">
                                            {headerGroup.headers.map(header => (
                                                <th
                                                    key={header.id}
                                                    className="text-left px-4 text-gray font-[Geist-Medium] text-xs md:text-sm bg-white"
                                                >
                                                    {header.isPlaceholder ? null : (
                                                        <div
                                                            onClick={header.column.getToggleSortingHandler()}
                                                            className="flex items-center cursor-pointer hover:text-green transition-colors"
                                                        >
                                                            {flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext()
                                                            )}
                                                            {header.column.getIsSorted() ? (
                                                                header.column.getIsSorted() === 'asc' ? (
                                                                    <Icon icon="solar:square-alt-arrow-up-bold" className="text-lg ml-1" />
                                                                ) : (
                                                                    <Icon icon="solar:square-alt-arrow-down-bold" className="text-lg ml-1" />
                                                                )
                                                            ) : null}
                                                        </div>
                                                    )}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {table.getRowModel().rows.map((row, index) => (
                                        <tr
                                            key={row.id}
                                            className={`h-16 border-t border-gray-3 text-gray-2 font-[Geist-Medium] text-xs md:text-sm ${index % 2 === 0 ? 'bg-white' : 'bg-gray-6'
                                                }`}
                                        >
                                            {row.getVisibleCells().map((cell: any) => (
                                                <td key={cell.id} className="px-4">
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="w-full rounded-xl h-40 flex justify-center items-center bg-white">
                        <span className="font-[Geist-Medium] text-sm text-gray-2">
                            No Data Found
                        </span>
                    </div>
                )}
            </div>

            {/* Pagination */}
            <div className="w-full flex justify-between items-center p-5 border-t border-gray-3">
                <div className="text-sm text-gray-2 font-[Geist-Medium]">
                    Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                    {Math.min(
                        (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                        table.getFilteredRowModel().rows.length
                    )}{' '}
                    of {table.getFilteredRowModel().rows.length} entries
                </div>

                {table.getPageCount() > 1 && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                            className={`flex items-center ${table.getCanPreviousPage()
                                ? 'text-green hover:text-green-600 cursor-pointer'
                                : 'text-gray-2 cursor-default'
                                }`}
                        >
                            <Icon icon="ic:sharp-chevron-left" className="text-3xl" />
                        </button>

                        <div className="flex items-center gap-2">
                            {[...Array(table.getPageCount())].map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => table.setPageIndex(index)}
                                    className={`w-9 h-9 flex justify-center items-center rounded-full font-[Geist-Medium] cursor-pointer transition-colors ${table.getState().pagination.pageIndex === index
                                        ? 'bg-green text-white'
                                        : 'bg-gray-3 text-gray-2 hover:bg-gray-2'
                                        }`}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                            className={`flex items-center ${table.getCanNextPage()
                                ? 'text-green hover:text-green-600 cursor-pointer'
                                : 'text-gray-2 cursor-default'
                                }`}
                        >
                            <Icon icon="ic:sharp-chevron-right" className="text-3xl" />
                        </button>
                    </div>
                )}
            </div>

            {/* Modals */}
            {viewSummary && <ViewSummary onClose={() => setViewSummary(false)} inspection={selectedInspection} />}
            {showInspectionModal && (
                <InspectionModal
                    isOpen={showInspectionModal}
                    onClose={() => setShowInspectionModal(false)}
                    onInspectionCreated={handleInspectionCreated}
                />
            )}
            {showSummary && <ViewSummary onClose={() => setShowSummary(false)} inspection={currentInspection} />}
        </div>
    );
}

const getStatusBadge = (status: 'in-progress' | 'completed' | 'cancelled' | 'failed') => {
    const getStatusConfig = (status: string) => {
        switch (status) {
            case "completed":
                return {
                    dotColor: "bg-green",
                    badgeClasses: "bg-green-50 text-green border-green-200",
                    text: "Successfully Completed"
                };
            case "in-progress":
                return {
                    dotColor: "bg-yellow",
                    badgeClasses: "bg-yellow-50 text-yellow border-yellow-200",
                    text: "Pending"
                };
            case "cancelled":
                return {
                    dotColor: "bg-gray-400",
                    badgeClasses: "bg-gray-50 text-gray-600 border-gray-200",
                    text: "Cancelled"
                };
            case "failed":
                return {
                    dotColor: "bg-red",
                    badgeClasses: "bg-red-50 text-red border-red-200",
                    text: "Failed"
                };
            default:
                return {
                    dotColor: "bg-gray-400",
                    badgeClasses: "bg-gray-50 text-gray-500 border-gray-200",
                    text: status
                };
        }
    };

    const config = getStatusConfig(status);

    return (
        <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${config.dotColor}`}></div>
            <span className={`px-3 py-1.5 rounded-full text-xs font-[Geist-SemiBold] border ${config.badgeClasses}`}>
                {config.text}
            </span>
        </div>
    );
};

export default InspectionPage;
